// ABOUTME: Coordinates a Kanban refresh after an applicant stage mutation.
// ABOUTME: Applies server data only after Elasticsearch exposes the expected stage.
import {
    ExpectedApplicantStage,
    waitForApplicantStages
} from "./applicantStageReadConsistency";

export async function refreshKanbanAfterStage({
    pendingStages,
    readOrderData,
    readAuthoritativeStage,
    loadAndApply,
    invalidate,
    isCurrent = () => true,
    wait
}: {
    pendingStages: {
        get: () => ExpectedApplicantStage[];
        clear: () => void;
        replace: (
            previousStage: ExpectedApplicantStage,
            nextStage: ExpectedApplicantStage
        ) => void;
    };
    readOrderData: () => Promise<any[]>;
    readAuthoritativeStage?: (
        expectedStage: ExpectedApplicantStage
    ) => Promise<{
        stageId?: string;
        requestId?: string;
    } | null>;
    loadAndApply: (orderData: any[]) => Promise<void>;
    invalidate: () => void;
    isCurrent?: () => boolean;
    wait?: (delayMs: number) => Promise<void>;
}): Promise<boolean> {
    const currentStages = pendingStages.get();
    if (
        currentStages.length === 0 ||
        currentStages.some((stage) => !stage.requestId)
    ) {
        pendingStages.clear();
        invalidate();
        return false;
    }

    const orderData = await waitForApplicantStages({
        getExpectedStages: pendingStages.get,
        readOrderData,
        shouldContinue: isCurrent,
        wait
    });
    if (!orderData) {
        if (!isCurrent()) {
            return false;
        }
        const authoritativeStages = await Promise.all(
            pendingStages.get().map(async (pendingStage) => ({
                pendingStage,
                authoritativeStage: readAuthoritativeStage
                    ? await readAuthoritativeStage(pendingStage).catch(
                          () => null
                      )
                    : null
            }))
        );
        if (!isCurrent()) {
            return false;
        }
        let stageWasSuperseded = false;
        for (const {
            pendingStage,
            authoritativeStage
        } of authoritativeStages) {
            if (
                authoritativeStage?.stageId &&
                authoritativeStage.requestId &&
                authoritativeStage.requestId !== pendingStage.requestId
            ) {
                pendingStages.replace(pendingStage, {
                    ...pendingStage,
                    stageId: authoritativeStage.stageId,
                    requestId: authoritativeStage.requestId
                });
                stageWasSuperseded = true;
            }
        }
        if (stageWasSuperseded) {
            const authoritativeOrderData = await waitForApplicantStages({
                getExpectedStages: pendingStages.get,
                readOrderData,
                shouldContinue: isCurrent,
                wait
            });
            if (authoritativeOrderData) {
                if (!isCurrent()) {
                    return false;
                }
                await loadAndApply(authoritativeOrderData);
                return true;
            }
        }
        if (!isCurrent()) {
            return false;
        }
        pendingStages.clear();
        invalidate();
        return false;
    }
    if (!isCurrent()) {
        return false;
    }
    await loadAndApply(orderData);
    return true;
}
